import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';          // ← NEW
import { Search } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';  // ← useLocation added

const SearchComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();                   // ← NEW
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);  // ← NEW: tracks input position
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // ─── FIX #2: Close dropdown whenever the route changes ───────────────────
  useEffect(() => {
    setShowSuggestions(false);
    setSuggestions([]);
    setQuery('');
  }, [location.pathname, location.search]);

  // ─── FIX #1: Recalculate input's position for the portal-rendered dropdown ─
  const updateDropdownRect = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownRect({
        top: rect.bottom + window.scrollY + 4,   // 4px gap below input
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.trim() === '') {
        setSuggestions([]);
        return;
      }
      setIsLoading(true);
      try {
        const response = await fetch(`https://flask.finstrats.com/search?key=${encodeURIComponent(query)}`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        setSuggestions(data || []);
        setShowSuggestions(true);
        updateDropdownRect();                     // ← capture position when results arrive
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      if (query.trim() !== '') fetchSuggestions();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Recompute position on scroll/resize so the portal dropdown tracks the input
  useEffect(() => {
    window.addEventListener('scroll', updateDropdownRect, true);
    window.addEventListener('resize', updateDropdownRect);
    return () => {
      window.removeEventListener('scroll', updateDropdownRect, true);
      window.removeEventListener('resize', updateDropdownRect);
    };
  }, []);

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    if (e.target.value.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    let navigationPath = '';
    let displayText = '';

    if (suggestion.company_name && suggestion.ticker_code) {
      navigationPath = `/stockdetails?ticker=${suggestion.ticker_code}`;
      displayText = `${suggestion.ticker_code}`;
    } else if (suggestion.name && suggestion.type) {
      switch (suggestion.type.toLowerCase()) {
        case 'stock':
          navigationPath = `/stockdetails?ticker=${suggestion.name}`;
          break;
        case 'industry':
          navigationPath = `/industry?name=${suggestion.name}`;
          break;
        default:
          navigationPath = `/search?q=${suggestion.name}`;
      }
      displayText = suggestion.name;
    } else if (typeof suggestion === 'string') {
      navigationPath = `/search?q=${suggestion}`;
      displayText = suggestion;
    } else {
      navigationPath = '/search';
      displayText = JSON.stringify(suggestion);
    }

    setQuery(displayText);
    setShowSuggestions(false);    // ← explicitly close before navigating
    navigate(navigationPath);
  };

  const getSuggestionDisplayText = (suggestion) => {
    if (typeof suggestion === 'object') {
      if (suggestion.company_name && suggestion.ticker_code) {
        return (
          <>
            <span style={styles.suggestionText}>{suggestion.company_name}</span>
            <span style={styles.suggestionType}>{suggestion.ticker_code}</span>
          </>
        );
      }
      if (suggestion.name && suggestion.type) {
        return (
          <>
            <span style={styles.suggestionText}>{suggestion.name}</span>
            <span style={styles.suggestionType}>{suggestion.type}</span>
          </>
        );
      }
      return JSON.stringify(suggestion);
    }
    return suggestion;
  };

  const handleSearch = () => {
    if (query.trim()) {
      setShowSuggestions(false);
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  // Responsive styles (unchanged from your original)
   const styles = {
    container: {
      position: 'relative',
      width: '100%', 
      maxWidth: '250px', 
      marginLeft: 'auto',
      padding: '0 10px', 
      boxSizing: 'border-box', 
    },
    inputWrapper: {
      position: 'relative',
      width: '100%',
    },
    input: {
      width: '100%',
      padding: '8px 30px 8px 10px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      boxSizing: 'border-box',
      backgroundColor: '#fff', // Ensure solid background
      color: '#000', // Ensure text is visible
    },
    searchIcon: {
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#888',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '30px', 
      height: '30px',
    },
    suggestionsDropdown: {
      position: 'absolute',
      top: 'calc(100% + 4px)', // Pushes it perfectly just below the input box
      left: '0',
      right: '0', // Stretches exactly to the bounds of the inputWrapper
      maxHeight: '300px', // Slightly taller to see more results
      overflowY: 'auto',
      backgroundColor: '#ffffff', // Force solid white so background doesn't bleed through
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.15)', // Strong drop shadow to pop over the tables below
      zIndex: 99999, // Guaranteed to float over all headers and charts
      boxSizing: 'border-box',
    },
    suggestionItem: {
      padding: '12px 10px', 
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap', 
      borderBottom: '1px solid #f3f4f6', // Clean separator between results
      color: '#111827',
    },
    suggestionItemHover: {
      backgroundColor: '#f3f4f6',
    },
    suggestionText: {
      flex: '1 1 auto', 
      minWidth: '60%', 
      wordBreak: 'break-word',
      fontWeight: '500', 
    },
    suggestionType: {
      fontSize: '11px',
      color: '#6b7280',
      marginLeft: '10px',
      flex: '0 0 auto', 
      backgroundColor: '#f3f4f6',
      padding: '2px 6px',
      borderRadius: '4px',
    },
  };

  // ─── Portal dropdown: rendered into document.body ─────────────────────────
  const dropdownPortal =
    showSuggestions && suggestions.length > 0 && dropdownRect
      ? createPortal(
          <div
            ref={suggestionsRef}
            style={{
              ...styles.suggestionsDropdown,
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
            }}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                style={styles.suggestionItem}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                {getSuggestionDisplayText(suggestion)}
              </div>
            ))}
          </div>,
          document.body                             // ← escape the navbar stacking context
        )
      : null;

  return (
    <>
      <div style={styles.container}>
        <div style={styles.inputWrapper}>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleQueryChange}
            onFocus={() => {
              if (query.trim() !== '') {
                updateDropdownRect();               // ← recompute on re-focus
                setShowSuggestions(true);
              }
            }}
            placeholder="Search..."
            style={styles.input}
          />
          <div style={styles.searchIcon} onClick={handleSearch}>
            <Search size={18} />
          </div>
        </div>
      </div>

      {dropdownPortal}                              {/* ← rendered outside navbar */}
    </>
  );
};

export default SearchComponent;


 
